// conversationAdapter/turnStreamCodec — the live turn-stream event → UI
// `TurnStreamEvent` codec, split out of `conversationAdapter.ts` (module-topology
// decomposition; verbatim).
//
// The SSE route (`packages/server/src/routes/turnStream.ts`) relays the RAW
// domain event JSON for the mid-turn progress types — no separate wire shape,
// same "the workflow's JSON IS the wire shape" convention every read in this
// codec family follows. This narrows that raw JSON into the UI's own
// `TurnStreamEvent` sum; an event type this stream doesn't know (or a shape
// mismatch on a known type) degrades to `undefined` — the caller (httpPorts)
// simply skips it, same total/never-throw discipline as `mkEntry`. Pure + total.

export { toTurnStreamEvent };

import type {
    ActionOfferEntry,
    ChatActionMove,
    ChatActionOfferTone,
    LiveActivity,
    MaterialKind,
} from "../../components/Chat/types.js";
import type {
    BuildPhase,
    MaterialBuildDelta,
    PlannedSlot,
    QualityBand,
    QualityMeasure,
    QualityMeasureDetail,
    QualityMeasureStatus,
    QualityVerdict,
    SlotKind,
} from "../materialBuildModel.js";
import type { TurnStreamEvent } from "../turnStream.js";
import { fields, list, num, str } from "../wireShape.js";
import { toInformationRequestEntry } from "./elicitation.js";
import { toToolStepOutcome } from "./toolCallDetail.js";

const toTurnStreamEvent = (raw: unknown): TurnStreamEvent | undefined => {
    const f = fields(raw);
    const type = str(f?.get("type"));
    switch (type) {
        case "agent_status_noted": {
            const id = str(f?.get("messageId"));
            const text = str(f?.get("status"));
            // An empty preamble ("the agent went straight to tool calls, no
            // commentary") has nothing to show — skip rather than render a
            // blank step.
            if (id === undefined || text === undefined || text === "") {
                return undefined;
            }
            return { type: "status", id, text };
        }
        case "tool_called": {
            const id = str(f?.get("toolCallId"));
            const name = str(f?.get("toolName"));
            if (id === undefined || name === undefined) return undefined;
            return { type: "tool_call", id, name };
        }
        // F124: the close carries its typed detail / failure reason.
        case "tool_call_completed":
        case "tool_call_failed": {
            const id = str(f?.get("toolCallId"));
            const outcome = toToolStepOutcome(f);
            if (id === undefined || outcome === undefined) return undefined;
            return { type: "tool_result", id, outcome };
        }
        // Transient reply token (producer: sendTurn's `onTextDelta` →
        // TurnStatusBus → SSE route's `agent_text_delta` wire shape). Carries
        // the message's app-minted identity (`messageId`) plus a monotonic
        // `seq`; the fold routes/orders by them, so a delta missing either is
        // unroutable and skipped (same total/degrade discipline as the rest).
        case "agent_text_delta": {
            const messageId = str(f?.get("messageId"));
            const seq = num(f?.get("seq"));
            const delta = str(f?.get("delta"));
            if (
                messageId === undefined ||
                seq === undefined ||
                delta === undefined
            )
                return undefined;
            return { type: "text_delta", messageId, seq, delta };
        }
        // Transient material-BUILD slice (producer: sendTurn's per-build emit →
        // TurnStatusBus → SSE route's `material_build_delta` wire shape). The
        // inner `delta` carries its OWN identity (buildId + seq); the fold routes/
        // orders by it, so a delta that fails to parse into the typed sum is
        // unroutable and skipped (same total/degrade discipline as the rest).
        case "material_build_delta": {
            const delta = toMaterialBuildDelta(f?.get("delta"));
            if (delta === undefined) return undefined;
            return { type: "material_build", delta };
        }
        // Transient ActionOffer epilogue (producer: sendTurn's `_offer_actions`
        // intercept / error derivation → TurnStatusBus → SSE `action_offer`
        // wire). Parsed into the UI `ActionOfferEntry` mirror; a shape miss
        // (no id / no valid move) is unroutable and skipped.
        case "action_offer": {
            const offer = toActionOfferEntry(f?.get("offer"));
            if (offer === undefined) return undefined;
            return { type: "action_offer", offer };
        }
        // Transient elicitation FORM epilogue (F3, producer: sendTurn's durable
        // `RequestInformation` commit → TurnStatusBus → SSE `information_request`
        // wire). Built into the SAME `InformationRequestEntry` the durable reload
        // projects (shared `toInformationRequestEntry`), keyed by the closing
        // message id, so live and reloaded forms dedup to one. A fresh form is
        // always `pending`; a shape miss (no messageId / no title) is skipped.
        case "information_request": {
            const id = str(f?.get("messageId"));
            if (id === undefined) return undefined;
            const request = toInformationRequestEntry(
                id,
                fields(f?.get("request")),
                { type: "pending" },
            );
            if (request === undefined) return undefined;
            return { type: "information_request", request };
        }
        // Transient form-PENDING marker (F53, producer: sendTurn's
        // `onToolUseStarted` for `_request_information` → TurnStatusBus → SSE
        // `information_request_pending` wire). Precedes the finished form by the
        // whole generation window; the fold shows a distinct form-load placeholder
        // keyed by `messageId` (the id the finished form also carries, so it
        // supersedes to one entry). A missing messageId is unroutable and skipped.
        case "information_request_pending": {
            const id = str(f?.get("messageId"));
            if (id === undefined) return undefined;
            const parts = list(f?.get("parts"))
                .map(str)
                .filter((p): p is string => p !== undefined);
            return { type: "information_request_pending", id, parts };
        }
        // Transient live activity (D51, producer: sendTurn's activity stream →
        // TurnStatusBus → SSE `agent_activity` wire). An unknown activity kind
        // is skipped; the live row then keeps its last label.
        case "agent_activity": {
            const activity = toLiveActivity(f?.get("activity"));
            if (activity === undefined) return undefined;
            return { type: "activity", activity };
        }
        default:
            return undefined;
    }
};

// Parse a wire `TurnActivity` into the UI mirror. Total — an unknown kind or a
// tool without a name yields undefined; a non-string object is dropped.
const toLiveActivity = (raw: unknown): LiveActivity | undefined => {
    const a = fields(raw);
    switch (str(a?.get("type"))) {
        case "thinking":
            return { type: "thinking" };
        case "writing_reply":
            return { type: "writing_reply" };
        case "tool": {
            const toolName = str(a?.get("toolName"));
            if (toolName === undefined) return undefined;
            const object = str(a?.get("object"));
            const materialKind = toMaterialKind(str(a?.get("materialKind")));
            return {
                type: "tool",
                toolName,
                ...(object === undefined ? {} : { object }),
                ...(materialKind === undefined ? {} : { materialKind }),
            };
        }
        default:
            return undefined;
    }
};

// A wire material kind, or undefined for anything this client doesn't know.
const toMaterialKind = (raw: string | undefined): MaterialKind | undefined =>
    raw === "course" || raw === "freeform" ? raw : undefined;

// Parse a wire `ActionOffer` into the UI `ActionOfferEntry` mirror. Total — a
// missing offerId or an empty/invalid move list yields undefined.
const toActionOfferEntry = (raw: unknown): ActionOfferEntry | undefined => {
    const o = fields(raw);
    const offerId = str(o?.get("offerId"));
    if (offerId === undefined) return undefined;
    const moves = list(o?.get("moves"))
        .map(toActionMove)
        .filter((m): m is ChatActionMove => m !== undefined);
    const [first, ...rest] = moves;
    if (first === undefined) return undefined;
    const tone: ChatActionOfferTone =
        str(o?.get("tone")) === "recovery" ? "recovery" : "neutral";
    return { type: "action_offer", id: offerId, tone, moves: [first, ...rest] };
};

const toActionMove = (raw: unknown): ChatActionMove | undefined => {
    const m = fields(raw);
    const type = str(m?.get("type"));
    const label = str(m?.get("label"));
    if (label === undefined) return undefined;
    switch (type) {
        case "open_material": {
            const materialId = str(m?.get("materialId"));
            return materialId === undefined
                ? undefined
                : { type: "open_material", label, materialId };
        }
        case "open_export": {
            const exportId = str(m?.get("exportId"));
            return exportId === undefined
                ? undefined
                : { type: "open_export", label, exportId };
        }
        case "download_export": {
            const exportId = str(m?.get("exportId"));
            return exportId === undefined
                ? undefined
                : { type: "download_export", label, exportId };
        }
        case "send_prompt": {
            const prompt = str(m?.get("prompt"));
            return prompt === undefined
                ? undefined
                : { type: "send_prompt", label, prompt };
        }
        case "request_upload": {
            const sourceId = str(m?.get("sourceId"));
            return sourceId === undefined
                ? undefined
                : {
                      type: "request_upload",
                      label,
                      sourceId,
                      sourceTitle: str(m?.get("sourceTitle")),
                  };
        }
        default:
            return undefined;
    }
};

// Parse an untrusted wire object into the typed `MaterialBuildDelta` sum, total
// and cast-free (wireShape narrowing + a `type`-discriminant match). Any shape
// mismatch — a missing identity field, an unknown `type`, an out-of-set phase —
// yields `undefined` so the caller skips it (never a throw, never a half-delta).
const toMaterialBuildDelta = (raw: unknown): MaterialBuildDelta | undefined => {
    const f = fields(raw);
    const type = str(f?.get("type"));
    const buildId = str(f?.get("buildId"));
    const seq = num(f?.get("seq"));
    const atMs = num(f?.get("atMs"));
    if (buildId === undefined || seq === undefined || atMs === undefined) {
        return undefined;
    }
    switch (type) {
        case "build_planned": {
            const title = str(f?.get("title"));
            if (title === undefined) return undefined;
            return {
                type: "build_planned",
                buildId,
                seq,
                atMs,
                title,
                slots: list(f?.get("slots"))
                    .map(toPlannedSlot)
                    .filter((s): s is PlannedSlot => s !== undefined),
            };
        }
        case "slot_started": {
            const slotId = str(f?.get("slotId"));
            if (slotId === undefined) return undefined;
            return { type: "slot_started", buildId, seq, atMs, slotId };
        }
        case "slot_filled": {
            const slotId = str(f?.get("slotId"));
            const text = str(f?.get("text"));
            if (slotId === undefined || text === undefined) return undefined;
            return { type: "slot_filled", buildId, seq, atMs, slotId, text };
        }
        case "phase_changed": {
            const phase = toBuildPhase(str(f?.get("phase")));
            if (phase === undefined) return undefined;
            return { type: "phase_changed", buildId, seq, atMs, phase };
        }
        case "build_evaluated": {
            const verdict = toQualityVerdict(f?.get("verdict"));
            if (verdict === undefined) return undefined;
            return { type: "build_evaluated", buildId, seq, atMs, verdict };
        }
        case "build_settled": {
            const materialId = str(f?.get("materialId"));
            const title = str(f?.get("title"));
            if (materialId === undefined || title === undefined) {
                return undefined;
            }
            return {
                type: "build_settled",
                buildId,
                seq,
                atMs,
                materialId,
                title,
            };
        }
        case "build_stopped":
            return { type: "build_stopped", buildId, seq, atMs };
        case "build_generating": {
            // G3-interim-A liveness pulse. `chars` is a cosmetic running tick;
            // a missing/invalid one degrades to 0 (the pulse still moves the
            // elapsed + phase), never dropped.
            const chars = num(f?.get("chars"));
            return {
                type: "build_generating",
                buildId,
                seq,
                atMs,
                chars: chars ?? 0,
            };
        }
        default:
            return undefined;
    }
};

const toSlotKind = (raw: string | undefined): SlotKind | undefined =>
    raw === "module" || raw === "question" ? raw : undefined;

const toBuildPhase = (raw: string | undefined): BuildPhase | undefined =>
    raw === "planning" ||
    raw === "drafting" ||
    raw === "grounding" ||
    raw === "finalizing"
        ? raw
        : undefined;

const toPlannedSlot = (raw: unknown): PlannedSlot | undefined => {
    const f = fields(raw);
    const id = str(f?.get("id"));
    const kind = toSlotKind(str(f?.get("kind")));
    if (id === undefined || kind === undefined) return undefined;
    const heading = str(f?.get("heading"));
    return heading === undefined ? { id, kind } : { id, kind, heading };
};

// Parse the tolerant Quality→Chat seam Verdict off the wire, mirroring
// packages/app/src/quality/verdict.ts's shape structurally (no import — the
// whole point of the seam is that @bilda/client never needs to understand a
// measure to carry/render it). `overall` is the one required field; a
// mismatched/unknown band or a missing `measures` list degrades to `[]`
// rather than dropping the whole verdict.
const toQualityBand = (raw: string | undefined): QualityBand | undefined =>
    raw === "blocked" ||
    raw === "weak" ||
    raw === "adequate" ||
    raw === "strong"
        ? raw
        : undefined;

const toQualityMeasureStatus = (
    raw: unknown,
): QualityMeasureStatus | undefined => {
    const f = fields(raw);
    switch (str(f?.get("type"))) {
        case "computed":
            return { type: "computed" };
        case "skipped": {
            const reason = str(f?.get("reason"));
            return reason === undefined
                ? undefined
                : { type: "skipped", reason };
        }
        case "failed": {
            const reason = str(f?.get("reason"));
            return reason === undefined
                ? undefined
                : { type: "failed", reason };
        }
        default:
            return undefined;
    }
};

const toQualityMeasureDetail = (
    raw: unknown,
): QualityMeasureDetail | undefined => {
    const f = fields(raw);
    const value = num(f?.get("value"));
    if (value === undefined) return undefined;
    const unit = str(f?.get("unit"));
    const target = num(f?.get("target"));
    return {
        value,
        ...(unit !== undefined ? { unit } : {}),
        ...(target !== undefined ? { target } : {}),
    };
};

const toQualityMeasure = (raw: unknown): QualityMeasure | undefined => {
    const f = fields(raw);
    const key = str(f?.get("key"));
    const labelKey = str(f?.get("labelKey"));
    const status = toQualityMeasureStatus(f?.get("status"));
    if (key === undefined || labelKey === undefined || status === undefined) {
        return undefined;
    }
    const band = toQualityBand(str(f?.get("band")));
    const detail = toQualityMeasureDetail(f?.get("detail"));
    return {
        key,
        labelKey,
        status,
        ...(band !== undefined ? { band } : {}),
        ...(detail !== undefined ? { detail } : {}),
    };
};

const toQualityVerdict = (raw: unknown): QualityVerdict | undefined => {
    const f = fields(raw);
    const overall = toQualityBand(str(f?.get("overall")));
    if (overall === undefined) return undefined;
    const measures = list(f?.get("measures"))
        .map(toQualityMeasure)
        .filter((m): m is QualityMeasure => m !== undefined);
    return { overall, measures };
};
